/**
 * Production adapter for `go_to_position`, using existing viewer calls only:
 *  - structure extent: the contour's first and last slices from
 *    `texture.signedDistanceField.getContentBounds(SDF_THRESHOLD)` (the
 *    call keep-every-Nth uses), converted to patient space at the exact
 *    slice centres with the texture grid — not the rotated bounding box,
 *    whose upper edge sits half a voxel beyond the last contour slice;
 *  - snapping and range: the image grid's `snapPositionToSliceCenter` (the
 *    call clear-current-slice uses) and `toVoxelSpace`;
 *  - moving: `mainView.updatePlaneIntersectionPoint`, the call the mouse
 *    wheel's ScrollAction makes; the zoom is untouched.
 * Browser glue covered by the manual demo; logic unit-tested through fakes.
 */
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { vec3 } from 'gl-matrix';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { environmentState, mainViewState } from '~/state/atoms/engineAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { projectStructureFacts } from '../core/engineStructureFacts';
import type { GoToPositionDeps, PatientPoint } from './goToPosition';

const asPoint = (v: ArrayLike<number>): PatientPoint => [v[0], v[1], v[2]];

export function useGoToPositionDeps(): () => GoToPositionDeps {
    const env = useRecoilValue(environmentState);
    const mainView = useRecoilValue(mainViewState);
    const structureSet = useRecoilValue(currentStructureSetState);

    return useCallback((): GoToPositionDeps => {
        const imageGrid = env?.image?.grid;
        return {
            viewReady: !!imageGrid && !!mainView,
            structures: projectStructureFacts(structureSet, env),
            structureEnds: (roiNumber) => {
                const texture = env?.getRoi(roiNumber)?.texture;
                const bounds = texture?.signedDistanceField?.getContentBounds(
                    ContourTexture.SDF_THRESHOLD
                );
                if (!texture || !bounds) {
                    return undefined;
                }
                const grid = texture.textureGrid;
                // Voxel i spans [i, i+1): its centre is i + 0.5; max is inclusive.
                const cx = (bounds.min[0] + bounds.max[0] + 1) / 2;
                const cy = (bounds.min[1] + bounds.max[1] + 1) / 2;
                const at = (z: number) =>
                    asPoint(grid.toWorldSpace(vec3.fromValues(cx, cy, z)));
                const first = at(bounds.min[2] + 0.5);
                const last = at(bounds.max[2] + 0.5);
                // "Top" is towards the head: the larger patient Z.
                const [top, bottom] =
                    first[2] >= last[2] ? [first, last] : [last, first];
                const middle = at((bounds.min[2] + bounds.max[2] + 1) / 2);
                return { top, middle, bottom };
            },
            currentPoint: () =>
                asPoint(env?.planeIntersectionPoint.coordinates ?? [0, 0, 0]),
            snapToSlice: (point) => {
                if (!imageGrid) {
                    return undefined;
                }
                const p = vec3.fromValues(point[0], point[1], point[2]);
                const z = imageGrid.toVoxelSpace(p)[2];
                if (!(z >= 0 && z < imageGrid.sizeZ)) {
                    return undefined;
                }
                return asPoint(imageGrid.snapPositionToSliceCenter(p));
            },
            scanRangeCm: () => {
                if (!imageGrid) {
                    return { fromCm: 0, toCm: 0 };
                }
                const zAt = (k: number) =>
                    imageGrid.toWorldSpace(vec3.fromValues(0, 0, k))[2] / 10;
                const a = zAt(0.5);
                const b = zAt(imageGrid.sizeZ - 0.5);
                return { fromCm: Math.min(a, b), toCm: Math.max(a, b) };
            },
            moveTo: (point) => {
                mainView?.updatePlaneIntersectionPoint(
                    vec3.fromValues(point[0], point[1], point[2])
                );
            },
        };
    }, [env, mainView, structureSet]);
}
